import React, { useCallback, useState, useEffect, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import { Form, Icon, List } from "semantic-ui-react";
import NumericInput from "../../../../components/shared/NumericInput";
import Panel from "../../../../components/shared/Panel";
import { useTextResources, useMessageResources } from "../../../../language";
import { DateRangeCreationPopup } from "./DateRangeCreationPopup";
import { PriceListContext } from "../../context";
import { weekDays, timeFormat, RateCategory, CollisionType } from "../../../../components/shared/constants";
import { onWarning } from '../../../../components/shared/messages';
import Moment from 'moment'
import { extendMoment } from 'moment-range';
import { HideControlsByRateType } from "../../HideControlsByRateType";
import { getCollisionType, toBitmask, makeDaysOfWeekArrayIntoInt, makeIntToDaysOfWeekArray } from '../../../../components/shared/utils'
import CheckBoxToggle from "../../../../components/shared/CheckBoxToggle";
import { useConfirm } from "../../../../hooks/useConfirm";
import Checkbox from "../../../../components/shared/Checkbox";

const moment = extendMoment(Moment);

const DEFAULT_LEAD_FIXED_DAYS_START = 0;
const DEFAULT_LEAD_FIXED_DAYS_END = 999;
const DEFAULT_MIN_NIGHT = 1;
const DEFAULT_MAX_NIGHT = 999;

const getDateWithTime = (date, time) => {
  const dateMoment = moment(date);
  const timeMoment = moment(time, timeFormat);
  dateMoment.set({
    'hour': timeMoment.hours(),
    'minute': timeMoment.minutes(),
    'second': timeMoment.seconds(),
    'millisecond': timeMoment.milliseconds()
  });
  return dateMoment;
};

const getOpenForSaleCondtionDateRange = (condition) => {
  return moment.range(
    getDateWithTime(condition.StartDate, condition.StartTime),
    getDateWithTime(condition.EndDate, condition.EndTime)
  );
};

export const AvailabilitySettings = () => {
  const message = useMessageResources();
  const text = useTextResources();
  const confirm = useConfirm();
  const { state, setState, setDirtyState } = useContext(PriceListContext);

  const {
    Id: rateId = undefined,
    EndDate: priceListEndDate = undefined,
    LeadFixedDaysStart: leadFixedDaysStart = DEFAULT_LEAD_FIXED_DAYS_START,
    LeadFixedDaysEnd: leadFixedDaysEnd = DEFAULT_LEAD_FIXED_DAYS_END,
    MinNights: minNights = DEFAULT_MIN_NIGHT,
    MaxNights: maxNights = DEFAULT_MAX_NIGHT,
    RateOpenForSaleConditions: rateOpenForSaleCondtions = [],
  } = state;

  const [isRateOpenForSaleConditionsChangable, setIsRateOpenForSaleConditionsChangable] = useState(false);
  const [isAddDateRangeDialogOpen, setIsAddDateRangeDialogOpen] = useState(false);
  const [includePastDates, setIncludePastDates] = useState(false);
  const openAddDateRangeDialog = useCallback(() => setIsAddDateRangeDialogOpen(true), []);
  const hideAddDateRangeDialog = useCallback(() => setIsAddDateRangeDialogOpen(false), []);

  const setContextState = useCallback((fieldName, value) => {
    setState((prevState) => ({ ...prevState, [fieldName]: value }));
    setDirtyState(prevState => ({ ...prevState, GeneralTab: true }));
  }, [setState, setDirtyState]);

  const updateContextState = useCallback((fieldName, updateFn) => {
    setState((prevState) => ({ ...prevState, [fieldName]: updateFn(prevState[fieldName]) }));
    setDirtyState(prevState => ({ ...prevState, GeneralTab: true }));
  }, [setState, setDirtyState]);

  useEffect(() => {
    const todayTimestamp = new Date().setHours(0, 0, 0, 0);
    const endDateTimestamp = priceListEndDate ? new Date(priceListEndDate).getTime() : Date.now();
    const isChangable = endDateTimestamp >= todayTimestamp;
    setIsRateOpenForSaleConditionsChangable(isChangable);
    if (!isChangable) {
      updateContextState("RateOpenForSaleConditions", (prevConditions) => prevConditions.filter(condition => condition.Id !== undefined));
    }
  }, [priceListEndDate, updateContextState]);

  const handleLeadFixedDaysStartChange = useCallback((value) => {
    if (Number.isInteger(value)) {
      setContextState("LeadFixedDaysStart", value);
    }
  }, [setContextState]);

  const handleLeadFixedDaysEndChange = useCallback((value) => {
    if (Number.isInteger(value)) {
      setContextState("LeadFixedDaysEnd", value);
    }
  }, [setContextState]);

  const handleMinNightsChange = useCallback((value) => {
    if (Number.isInteger(value)) {
      setContextState("MinNights", value);
    }
  }, [setContextState]);

  const handleMaxNightsChange = useCallback((value) => {
    if (Number.isInteger(value)) {
      setContextState("MaxNights", value);
    }
  }, [setContextState]);

  const handleRemoveRateOpenForSaleCondtion = useCallback((conditionToRemove) => {
    updateContextState("RateOpenForSaleConditions", (prevConditions) => {
      return conditionToRemove.Id !== undefined
        ? prevConditions.map((condition) => (condition.Id === conditionToRemove.Id ? { ...condition, Id: -condition.Id } : condition))
        : prevConditions.filter((condition) => (condition.TempId !== conditionToRemove.TempId))
    });
  }, [updateContextState]);

  const areDatesOverlapping = useCallback(
    (newItemStartEndDateTimeRange, existingOpenForSaleConditionItems) => {
      if (existingOpenForSaleConditionItems?.length) {
        const hasOverlappingDates = existingOpenForSaleConditionItems
          .filter(({ condition: item }) => item.IsActive)
          .map(({ condition: item }) =>
            getCollisionType(
              {
                EndDate: item.EndDate,
                StartDate: item.StartDate,
                Weekdays: item.Weekdays,
                StartTime: item.StartTime,
                EndTime: item.EndTime,
              },
              {
                StartDate: newItemStartEndDateTimeRange.start,
                EndDate: newItemStartEndDateTimeRange.end,
                Weekdays: newItemStartEndDateTimeRange.weekdays,
                StartTime: newItemStartEndDateTimeRange.start.format("HH:mm"),
                EndTime: newItemStartEndDateTimeRange.end.format("HH:mm"),
              }
            )
          )
          .some((collisionType) => collisionType !== CollisionType.NO_COLLISION);

        return hasOverlappingDates;
      }
      return false;
    },
    []
  );

  const rateOpenForSaleConditionListItems = useMemo(() => {
    const visibleConditions = rateOpenForSaleCondtions.filter(condition => (condition.Id === undefined || condition.Id >= 0));
    // Historical data retention policy: show up to 3 years of past availability conditions
    const PAST_DATES_HISTORICAL_CUTOFF_YEARS = 3;
    const threeYearsAgo = moment().subtract(PAST_DATES_HISTORICAL_CUTOFF_YEARS, 'years');

    let visibleOpenForSaleListItems = visibleConditions.map((condition) => {
      const startEndDateTimeRange = getOpenForSaleCondtionDateRange(condition);
      return {
        condition: {
          ...condition,
          IsInThePast: startEndDateTimeRange.end.isBefore(moment(), 'day'),
          IsOlderThan3Years: startEndDateTimeRange.end.isBefore(threeYearsAgo, 'day'),
          Weekdays: makeIntToDaysOfWeekArray(condition.Weekdays)
        },
        startEndDateTimeRange: startEndDateTimeRange,
      };
    });

    if (includePastDates) {
      visibleOpenForSaleListItems = visibleOpenForSaleListItems.filter(item => !item.condition.IsOlderThan3Years);
    }
    else {
      visibleOpenForSaleListItems = visibleOpenForSaleListItems.filter(item => !item.condition.IsInThePast);
    }
    visibleOpenForSaleListItems.sort((left, right) => {
      return left.startEndDateTimeRange.start.valueOf() - right.startEndDateTimeRange.start.valueOf();
    });
    return visibleOpenForSaleListItems;
  }, [rateOpenForSaleCondtions, includePastDates]);

  const handleAddRateOpenForSaleCondition = useCallback(
    (newCondition) => {
      const timeDateProperties = getOpenForSaleCondtionDateRange(newCondition);
      if (
        areDatesOverlapping(
          { ...timeDateProperties, weekdays: newCondition.Weekdays },
          rateOpenForSaleConditionListItems
        )
      ) {
        onWarning(message('ErrorOverlappingDates'));
        return;
      }
      updateContextState('RateOpenForSaleConditions', (prevCondtions) => [
        ...prevCondtions,
        {
          ...newCondition,
          TempId: `newRateConditionTempId-${crypto.randomUUID()}`,
          Weekdays: makeDaysOfWeekArrayIntoInt(newCondition.Weekdays),
          ...toBitmask(newCondition.Weekdays)
        },
      ]);
      hideAddDateRangeDialog();
    },
    [
      message,
      rateOpenForSaleConditionListItems,
      updateContextState,
      areDatesOverlapping,
    ]
  );

  const changeConsiderOptimaLiveMinMaxNights = async (value) => {
    if (value) {
      await confirm({
        header: text('Warning'),
        content: message('ConsiderLiveMinMaxNightsEnableMessage'),
        confirmButton: {
          content: text('Yes'),
          positive: true,
        },
        cancelButton: {
          content: text('No'),
          negative: true,
        },
        onConfirm: async () => setContextState("ConsiderLiveMinMaxNights", true)
      });
    }
    else setContextState("ConsiderLiveMinMaxNights", false)
  }

  return (
    <Panel header={text("AvailabilitySettings")} info="PriceListAvailabilitySettingsInfo">
      <Form size="small">
        <Form.Group >
          <Form.Field width={6} style={{ display: 'flex', alignItems: 'center' }}>
            <div>{`${text("Number")} ${text("DaysBefore")} ${text("CheckIn")}`}</div>
          </Form.Field>
          <Form.Field
            width={5}
            control={NumericInput}
            prefix={text("Min")}
            buttonPlacement="right"
            digits={0}
            onChange={handleLeadFixedDaysStartChange}
            value={leadFixedDaysStart}
            min={DEFAULT_LEAD_FIXED_DAYS_START}
            max={leadFixedDaysEnd}
          />
          <Form.Field
            width={5}
            control={NumericInput}
            prefix={text("Max")}
            buttonPlacement="right"
            digits={0}
            onChange={handleLeadFixedDaysEndChange}
            value={leadFixedDaysEnd}
            min={0}
            max={DEFAULT_LEAD_FIXED_DAYS_END}
          />
        </Form.Group>
        <HideControlsByRateType rates={[RateCategory.ExtraAddOn, RateCategory.DayUse]}>
          <Form.Group>
            <Form.Field width={6} style={{ display: 'flex', alignItems: 'center' }}>
              <span>{text("Nights")}</span>
            </Form.Field>
            <Form.Field
              width={5}
              control={NumericInput}
              prefix={text("Min")}
              digits={0}
              buttonPlacement="right"
              onChange={handleMinNightsChange}
              value={minNights}
              min={DEFAULT_MIN_NIGHT}
              max={maxNights}
            />
            <Form.Field
              width={5}
              control={NumericInput}
              prefix={text("Max")}
              digits={0}
              buttonPlacement="right"
              onChange={handleMaxNightsChange}
              value={maxNights}
              min={DEFAULT_MIN_NIGHT}
              max={DEFAULT_MAX_NIGHT}
            />
          </Form.Group>
          <div className="checkbox-wrapper">
            {text("ConsiderOptimaLiveMinMaxNights")}
            <CheckBoxToggle
              onClick={() => changeConsiderOptimaLiveMinMaxNights(!state.ConsiderLiveMinMaxNights)}
              checked={state.ConsiderLiveMinMaxNights}
            />
          </div>
        </HideControlsByRateType>
        <HideControlsByRateType rates={[RateCategory.DayUse]}>
          {isRateOpenForSaleConditionsChangable && (<div className="open-for-sale-conditions">
            <div className="add-dates-range-link">
              <a
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={openAddDateRangeDialog}
              >
                {text("Add")} {text("DatesRange")}
              </a>

              <DateRangeCreationPopup
                RateId={rateId}
                open={isAddDateRangeDialogOpen}
                onClose={hideAddDateRangeDialog}
                onAdd={handleAddRateOpenForSaleCondition}
              />
            </div>
            <div className="include-past-dates-checkbox">
              <Checkbox
                label={text("IncludePastDates")}
                onChange={setIncludePastDates}
                checked={includePastDates}
              />
            </div>
          </div>)}
        </HideControlsByRateType>
        <div style={{ height: 180, overflow: "auto" }}>
          <List divided>
            {rateOpenForSaleConditionListItems.map((item, index) => (
              <OpenForSaleConditionListItem
                key={index}
                {...item}
                onRemove={handleRemoveRateOpenForSaleCondtion}
              />)
            )}
          </List>
        </div>
      </Form>
    </Panel>
  );
}

const OpenForSaleConditionListItem = ({
  condition,
  startEndDateTimeRange,
  onRemove,
}) => {
  const text = useTextResources();
  const handleRemove = useCallback(() => onRemove(condition), [condition, onRemove]);

  return (
    <List.Item key={startEndDateTimeRange.start.valueOf()}
      className={condition.IsInThePast ? 'past-item' : ''}>
      <List.Content floated="right">
        {!condition.IsInThePast && <Icon
          name="delete"
          link
          onClick={handleRemove}
        />}
      </List.Content>
      <List.Header>{`${text("From")} ${startEndDateTimeRange.start.toDate()?.toLocaleDateString("en-GB")} ${text(
        "To"
      )} ${startEndDateTimeRange.end.toDate()?.toLocaleDateString("en-GB")}`}</List.Header>
      <List.Description>{`From ${moment(condition.StartTime, timeFormat).format(timeFormat)} To ${moment(condition.EndTime, timeFormat).format(timeFormat)}`}</List.Description>
      {condition.Weekdays?.length && <List.Content>
        {condition.Weekdays.map(
          (day) => text(weekDays[day])
        ).join(", ")}
      </List.Content>}
    </List.Item>
  )
}

OpenForSaleConditionListItem.propTypes = {
  condition: PropTypes.shape({
    StartTime: PropTypes.string.isRequired,
    EndTime: PropTypes.string.isRequired,
    Weekdays: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.arrayOf(PropTypes.number).isRequired]),
  }),
  onRemove: PropTypes.func.isRequired,
  startEndDateTimeRange: PropTypes.object.isRequired,
};

AvailabilitySettings.propTypes = {};
